import { MissingDetails, ModelStateErrors } from "@core/models/error";
import { SelectionOption } from "@core/models/shared";
import { EmployerContacts } from "./employer-contacts.model";
import { EmployerLeaveAdminInfo } from "./employer-leave-admin.model";
import { EmployerNote } from "./employer-note.model";
import { StateAbbreviation } from "./state-abbreviation.model";
import { EmployerLogo } from "./employer-logo.model";
import { Banks } from "@core/enums/banks.enum";
import { Buckets } from "@core/enums";
import { DurationType } from "@core/enums/duration-type.enum";
import { MeasuredForwardStart } from "@core/enums/measured-forward-start.enum";
import { QuantityMeasure } from "@core/enums/entitlement-rule.enum";

export interface EmployerProfile {
    showDuplicateDialog?: boolean //not used - needed for service call that also returns interface type DuplicateDetectedDialog
    showUpdateConfirmationDialog?: boolean //not used - needed for service call that also returns interface type UpdateConfirmationDialog
    employerId: number;
    isTenantEmployer: boolean;
    isIndependentEmployer: boolean;
    companyName: string;
    customSubdomainName: string; //nullable
    customEmployerLogo: EmployerLogo; //nullable
    headcount: number; //nullable
    allowAccess: boolean;
    showAllowAccess: boolean;
    allowNewLeaves: boolean;
    employerContacts: EmployerContacts; //nullable
    headquartersState: StateAbbreviation; //nullable
    applicableStates: StateAbbreviation[]; //nullable

    leaveHoursBanks: EmployerLeaveHoursBank[];
    leaveHoursBuckets: EmployerLeaveHoursBucket[];

    leaveAdminInfo: EmployerLeaveAdminInfo;
    
    leaveYearRules: LeaveYearRule[];
    leaveYearRulesUpdateStartTime: Date; //nullable

    leaveTypes: EmployerLeaveType[];

    leaveTypeCatNames: LeaveTypeCatNames;

    employerNotes: EmployerNote[]; //nullable
    modelStateErrors: ModelStateErrors; //nullable
    documents: EmployerDocument[]; //nullable
    
    restrictToWorkEmail: boolean; //nullable
    showRestrictToWorkEmailInherited: boolean; //nullable

    automaticCcAddresses: string; //nullable
    automaticBccAddresses: string; //nullable

    integrations: Integration[]; //nullable
    notifications: EmployerNotification[];

    stiiraDomain: string;

    missingDetails: MissingDetails;
}

export interface RestoreCondition {
    employerLeaveYearRulesRestoreConditionID: number;
}

export interface EmployerLeaveHoursBank {
    isBank: boolean;
    id: Banks;
    defaultName: string;
    primaryName: string; //nullable
    primaryFullDayIncrements: boolean;
    leaveYearRule: string; //nullable
    leaveYearRuleEffectiveDate: Date; //nullable
    stateSpecificNames: LeaveHoursBankStateName[];
    leaveYearRules: LeaveHoursBankLeaveYearRule[];
    entitlementRules: LeaveHoursBankEntitlementRule[];
    
    // bank options
    isProtected: boolean;
}

export interface LeaveHoursBankStateName {
    bank: Banks;
    stateSpecificName: string;
    stateId: number;
    stateName: string;
    stateFullDayIncrements: boolean;
}

export interface LeaveHoursBankLeaveYearRule {
    bank: Banks;
    stateId: number; //nullable
    stateName: string; //nullable
    leaveYearRule: string;
    leaveYearRuleEffectiveDate: Date; //nullable
}

export interface LeaveHoursBankEntitlementRule {
    id: number;
    bank: Banks;
    state: SelectionOption; //nullable
    leaveReason: SelectionOption; //nullable
    quantity: number;
    quantityMeasure: QuantityMeasure;
    oneTimeEntitlement: boolean;
}

export interface EmployerLeaveHoursBucket {
    id: Buckets;
    defaultName: string;
    primaryName: string; //nullable
    leaveYearRule?: string; //nullable (only used to display N/A by the front end)
    stateSpecificNames: LeaveHoursBucketStateName[];
}

export interface LeaveHoursBucketStateName {
    bucket: Buckets;
    stateSpecificName: string; 
    stateId: number;
    stateName: string;
}

export interface EmployerLeaveType {
    id: number;
    name: string;
    leaveCategory: SelectionOption;
    banks: Banks[];
    buckets: Buckets[];
    isContinuous: boolean;
    isIntermittent: boolean;
    leavesRequireAllBankHours: boolean;
    allowUse: boolean;
    canDelete: boolean;
    stateVersions: string[];
}

export interface LeaveTypeCatNames {
    leaveCategories: CustomName[];
}

export interface CustomName {
    id: number;
    name: string;
    customName: string; //nullable
}

export interface LeaveYearRule {
    leaveYearRuleId: number;
    effectiveDate: Date; //nullable
    bank: Banks; //nullable
    bankName: string; //nullable
    workingInState: SelectionOption; //nullable
    leaveYear: SelectionOption; //nullable
    
    durationType: DurationType;
    duration: number; //nullable

    measuredForwardStart: MeasuredForwardStart; //nullable
    fixedStart: string; //nullable

    restoreHours: boolean; //nullable
    restoreConditions: SelectionOption[]; //nullable

    followFmlaUsageRules: boolean;
    isFullWeekClosureWorkdaysOnly: boolean; //nullable
    isFullWeekAbsenceWorkdaysOnly: boolean; //nullable
}

export class EmployerDocument { //must be a class instead of an interface for type comparison
    employerDocumentId: number; 
    documentTitle: string;
    documentCategoryId: number;
    documentCategory: string;
    createDateTime: Date;
    uploadedBy: SelectionOption;
    documentComments: string; //nullable
    canDelete: boolean;
    canEdit: boolean;
    canView: boolean;
    fileExtension: string;
}

export class Integration {
    name: string;
    description: string;
    enabled: boolean;

    fields: IntegrationField[]; //nullable

    automations: Automation[]; //nullable
}

export class IntegrationField {
    name: string;
    value: string; //nullable
    fieldTypeId: number;

    hasValue: boolean;
}

export class AutomationJob {
    startTime: Date; //nullable
    endTime: Date; //nullable
    successful: boolean; //nullable
    recordsProcessed: number; //nullable
}

export class Automation {
    name: string;
    description: string;
    enabled: boolean;

    lastJob: AutomationJob;
    lastSuccessfulJob: AutomationJob;
    lastSuccessfulJobWithRecords: AutomationJob;

    runFrequencyDescription: string; //nullable

    fields: AutomationField[]; //nullable
    fieldXrefGroup: AutomationFieldXrefGroup[]; //nullable
}

export class AutomationField {
    name: string;
    value: string; //nullable
    fieldTypeId: number;

    hasValue: boolean;
}

export class AutomationFieldXrefGroup {
    name: string;

    keyTitle: string;
    valueTitle: string;

    fields: AutomationFieldXref[]; //nullable
}

export class AutomationFieldXref {
    key: string;
    value: string; //nullable
}

export interface EmployerNotification {
    notificationTypeId: number;
    name: string;
    description: string;
    enabled: boolean;
    emailType: string;

    alwaysEnabled: boolean;
    defaultEmailTemplateId: number; //nullable
    defaultRecipientTypeId: number; //nullable
    defaultAdditionalRecipientTypeId: number; //nullable
    defaultEmailAddressTypeId: number; //nullable

    usingDefaults: boolean; //nullable

    emailTemplate: SelectionOption; //nullable
    toRecipients: EmployerNotificationRecipient[]; //nullable
    ccRecipients: EmployerNotificationRecipient[]; //nullable
    bccRecipients: EmployerNotificationRecipient[]; //nullable
}

export enum EmployerNotificationRecipientType {
    recipientType = 1,
    specificEmployee = 2,
    specificEmailAddress = 3
}

export interface EmployerNotificationRecipient {
    type: EmployerNotificationRecipientType;
    id: number; //nullable - used for either contactType/contact id
    title: string; // if specificEmailAddress used for email address
    companyName: string; //nullable - when appropriate, supplied with title when title is employee name
    description: string;
    emailAddressTypeId: number; //nullable - set when type is recipient type or specific employee
}